/**
 * Property showing requests — typed helpers over the shared ApiClient.
 *
 * The client submits an address (resolved via Google Places — we hold no MLS
 * data) + a preferred date/time; it lands as a PENDING appointment the agent
 * confirms. "My showing requests" polls the same list.
 */

import { api } from "../../lib/api";

export interface AppointmentRequest {
  id: string;
  status: string;
  appointment_type: string;
  property_address: string | null;
  place_id: string | null;
  latitude: number | null;
  longitude: number | null;
  starts_at: string;
  ends_at: string;
  notes: string | null;
  client_id: string | null;
  client_user_id: string | null;
  client_name: string | null;
  agent_id: string | null;
  agent_name: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateAppointmentRequest {
  property_address: string;
  place_id?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  /** ISO 8601 preferred date/time. */
  starts_at: string;
  notes?: string | null;
}

/** The caller's own showing requests, newest first. */
export function listMyAppointments(): Promise<AppointmentRequest[]> {
  return api.request<AppointmentRequest[]>("/appointment/requests");
}

/** Submit a new PENDING showing request. */
export function createAppointmentRequest(
  body: CreateAppointmentRequest,
): Promise<AppointmentRequest> {
  return api.request<AppointmentRequest>("/appointment/requests", {
    method: "POST",
    body,
  });
}

/**
 * What a reschedule/cancel cost the client (V053).
 *
 * A change inside 24h of a CONFIRMED showing is "late": three in a row and the
 * client can no longer book until their agent re-enables it.
 */
export interface RescheduleResult {
  appointment: AppointmentRequest;
  was_late: boolean;
  hours_notice: number;
  late_change_streak: number;
  showings_disabled: boolean;
}

/**
 * Propose a new time. The OTHER side confirms it: a client's move drops the
 * viewing back to PENDING for the agent, an agent's move makes it PROPOSED for
 * the client.
 */
export function rescheduleAppointment(
  id: string,
  startsAt: string,
): Promise<RescheduleResult> {
  return api.request<RescheduleResult>(
    `/appointment/requests/${id}/reschedule`,
    { method: "PATCH", body: { starts_at: startsAt } },
  );
}

/** Client accepts the time their agent proposed. */
export function acceptProposedTime(id: string): Promise<AppointmentRequest> {
  return api.request<AppointmentRequest>(`/appointment/requests/${id}/accept`, {
    method: "PATCH",
  });
}

/** Client cancels a viewing (inside 24h of a confirmed one, this is a strike). */
export function cancelAppointment(id: string): Promise<RescheduleResult> {
  return api.request<RescheduleResult>(`/appointment/requests/${id}/cancel`, {
    method: "PATCH",
  });
}
